// Central axios instance — the only place HTTP config lives.
// All services import this; never create raw axios instances elsewhere.

import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';

import { API_BASE_URL } from '@/constants/api';
import { ERROR_MESSAGES, DEFAULT_ERROR_MESSAGE } from '@/constants/errors';
import { getStoredRefreshToken } from '@/lib/auth/tokens';
import { useAuthStore } from '@/stores/auth.store';
import type { BackendErrorResponse, WTSApiError } from '@/types/api';

// ── Auth handler registration ──
// _onUnauthorized is injected by AppProviders so the interceptor can trigger
// logout + redirect without importing React or router here.
// _getToken is NO LONGER used — the interceptor reads from the Zustand store
// directly (useAuthStore.getState().accessToken). This is HMR-safe: module-level
// variables like _getToken reset on every hot reload; the Zustand store does not.

type UnauthorizedFn = () => void;

let _onUnauthorized: UnauthorizedFn = () => {};

export function setupApiClientAuth(
  _unusedGetToken: () => string | null,
  onUnauthorized: UnauthorizedFn,
): void {
  _onUnauthorized = onUnauthorized;
}

// ── Concurrent 401 request queuing ──
// While a token refresh is in-flight, subsequent 401 requests are queued.
// On success → all queued requests replay with the new token.
// On failure → all queued requests are rejected.

let _refreshing = false;
let _refreshQueue: Array<{ resolve: (token: string) => void; reject: (err: unknown) => void }> = [];

function enqueueRefresh(): Promise<string> {
  return new Promise((resolve, reject) => {
    _refreshQueue.push({ resolve, reject });
  });
}

function drainQueue(token: string): void {
  _refreshQueue.forEach((q) => q.resolve(token));
  _refreshQueue = [];
}

function rejectQueue(err: unknown): void {
  _refreshQueue.forEach((q) => q.reject(err));
  _refreshQueue = [];
}

// ── Helper: extract error from backend error envelope ──
// Handles two formats:
//   1. StandardResponse error: { success: false, error: { code, message } }
//   2. FastAPI Pydantic validation: { detail: [{ loc, msg, type }] }
function normaliseError(body: BackendErrorResponse | undefined, httpStatus: number): WTSApiError {
  const code = body?.error?.code ?? '';
  const rawMessage = body?.error?.message ?? '';

  // FastAPI validation errors use { detail: [{loc, msg, type}] } — not StandardResponse.
  const detail = (body as Record<string, unknown> | undefined)?.detail;
  const validationMessage =
    Array.isArray(detail) && detail.length > 0
      ? String((detail[0] as Record<string, unknown>)?.msg ?? '')
      : '';

  const effectiveCode =
    code || (validationMessage ? 'INVALID_INPUT' : httpStatus ? `HTTP_${httpStatus}` : 'NETWORK_ERROR');
  const message =
    ERROR_MESSAGES[code] ?? (rawMessage || validationMessage || DEFAULT_ERROR_MESSAGE);

  return { code: effectiveCode, message, status: httpStatus, details: body?.error?.details };
}

// ── Axios instance ──

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// ── Request interceptor — attach Bearer token ──
// Reads the access token directly from the Zustand store rather than via an
// injected getter. This survives Next.js HMR: when client.ts is hot-reloaded
// the store is NOT re-created, so getState().accessToken is always current.

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().accessToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Let the browser set Content-Type + boundary for multipart uploads automatically.
    // The global default 'application/json' would break FormData requests.
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// ── Response interceptor — unwrap StandardResponse<T> & normalise errors ──
// Backend success format: { success: true, data: T }
// Backend error format:   { success: false, error: { code, message } }  ← nested!

apiClient.interceptors.response.use(
  (response) => {
    // Unwrap the StandardResponse envelope — callers receive T directly.
    // We return response.data (not response) so `await apiClient.get/post(...)`
    // resolves to T, matching the <never, T> generic the callers declare.
    const body = response.data as { success?: boolean; data?: unknown };
    if (body && body.success === true && 'data' in body) {
      response.data = body.data;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return response.data as any;
  },
  async (error: AxiosError<BackendErrorResponse>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const status = error.response?.status ?? 0;
    const body = error.response?.data;

    const apiError = normaliseError(body, status);

    if (status === 401 && !originalRequest._retry) {
      // Fast-path: non-retryable auth failures — skip refresh and log out immediately.
      // ACCOUNT_SUSPENDED / SESSION_REVOKED won't be fixed by a new access token.
      if (
        apiError.code === 'ACCOUNT_SUSPENDED' ||
        apiError.code === 'SESSION_REVOKED'
      ) {
        _onUnauthorized();
        return Promise.reject(apiError);
      }

      // Guard: only attempt token refresh when the user has an active session
      // (i.e. a stored refresh token exists). Without this guard, a 401 from a
      // public auth endpoint such as POST /auth/login (INVALID_CREDENTIALS) or
      // POST /auth/mfa/verify (INVALID_MFA_TOKEN) would trigger the refresh flow,
      // which then calls _onUnauthorized() → page reload → form cleared.
      if (!getStoredRefreshToken()) {
        return Promise.reject(apiError);
      }

      // Another refresh is already in-flight — queue this request.
      if (_refreshing) {
        try {
          const newToken = await enqueueRefresh();
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return apiClient(originalRequest);
        } catch {
          return Promise.reject(apiError);
        }
      }

      originalRequest._retry = true;
      _refreshing = true;

      try {
        const newToken = await useAuthStore.getState().refreshAccessToken();

        if (newToken) {
          drainQueue(newToken);
          _refreshing = false;
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return apiClient(originalRequest);
        }
      } catch {
        // Refresh threw — fall through to unauthorised handler.
      }

      _refreshing = false;
      rejectQueue(apiError);
      _onUnauthorized();
    }

    return Promise.reject(apiError);
  },
);

export default apiClient;
